import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import mapboxgl from 'mapbox-gl'
import BaseMap from '../common/BaseMap'
import LocationSearch from '../common/LocationSearch'
import RadiusCircle from '../common/RadiusCircle'
import { theme } from '../../config/theme'

const SaleMap = ({ eventLocation, radius }) => {
  const eventMarker = useRef(null)
  const saleMarker = useRef(null)
  const [searchError, setSearchError] = useState(null)
  const [mapInstance, setMapInstance] = useState(null)
  
  const { setValue, watch, formState } = useFormContext()
  const location = watch('location')

  // Calculate distance between two points
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 3958.8 // Earth's radius in miles
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }, [])

  const toRad = (value) => {
    return (value * Math.PI) / 180
  }

  // Check if a location is within the event radius
  const isWithinRadius = useCallback((lat, lng) => {
    if (!eventLocation || !radius) return true
    
    const distance = calculateDistance(
      parseFloat(eventLocation.latitude),
      parseFloat(eventLocation.longitude),
      lat,
      lng
    )
    
    return distance <= parseFloat(radius)
  }, [eventLocation, radius, calculateDistance])

  // Handle map click for location selection
  const handleMapClick = useCallback(async (e) => {
    const { lng, lat } = e.lngLat
    
    // Check if location is within radius
    if (!isWithinRadius(lat, lng)) {
      setSearchError(`Please select a location within ${radius} miles of the event.`)
      return
    }
    
    setSearchError(null)
    
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}&types=address&limit=1&autocomplete=false`
      )
      const data = await response.json()
      
      if (data.features && data.features.length > 0) {
        const feature = data.features[0]
        const locationData = {
          address: feature.place_name.split(',')[0].trim(),
          city: feature.context?.find(c => c.id.startsWith('place'))?.text || '',
          state: feature.context?.find(c => c.id.startsWith('region'))?.short_code?.replace(/^US-/, '') || feature.context?.find(c => c.id.startsWith('region'))?.text || '',
          zip_code: feature.context?.find(c => c.id.startsWith('postcode'))?.text || '',
          latitude: lat.toString(),
          longitude: lng.toString()
        }
        
        setValue('location', locationData, { shouldValidate: true })
        updateSaleMarker(lng, lat)
      }
    } catch (error) {
      console.error('Error geocoding location:', error)
    }
  }, [setValue, radius, isWithinRadius])

  // Handle location search selection
  const handleLocationSearchSelect = useCallback((locationData, error) => {
    if (error) {
      setSearchError(error)
      return
    }
    
    setSearchError(null)
    
    if (locationData) {
      setValue('location', locationData, { shouldValidate: true })
      
      const lng = parseFloat(locationData.longitude)
      const lat = parseFloat(locationData.latitude)
      
      updateSaleMarker(lng, lat)
      
      // Fly to location
      if (mapInstance) {
        mapInstance.flyTo({
          center: [lng, lat],
          zoom: 15
        })
      }
    }
  }, [setValue, mapInstance])

  // Update sale marker position
  const updateSaleMarker = useCallback((lng, lat) => {
    if (!mapInstance) return

    // Remove existing sale marker
    if (saleMarker.current) {
      saleMarker.current.remove()
      saleMarker.current = null
    }

    // Create new sale marker
    saleMarker.current = new mapboxgl.Marker({ color: theme.colors.red[500] })
      .setLngLat([lng, lat])
      .addTo(mapInstance)
  }, [mapInstance])

  // Handle map ready
  const handleMapReady = useCallback((mapInstance) => {
    setMapInstance(mapInstance)
  }, [])

  // Initialize event marker when map is ready
  useEffect(() => {
    if (!mapInstance || !eventLocation) return

    const { latitude, longitude } = eventLocation
    const lat = parseFloat(latitude)
    const lng = parseFloat(longitude)

    if (!isNaN(lat) && !isNaN(lng)) {
      // Create event marker (fixed position)
      if (eventMarker.current) {
        eventMarker.current.remove()
      }
      
      eventMarker.current = new mapboxgl.Marker({ color: theme.colors.primary[500] })
        .setLngLat([lng, lat])
        .addTo(mapInstance)

      // Center map on event
      mapInstance.flyTo({
        center: [lng, lat],
        zoom: 12
      })
    }
  }, [mapInstance, eventLocation])

  // Update sale marker when location changes
  useEffect(() => {
    if (!mapInstance || !location?.latitude || !location?.longitude) return

    const lng = parseFloat(location.longitude)
    const lat = parseFloat(location.latitude)
    
    if (!isNaN(lng) && !isNaN(lat)) {
      updateSaleMarker(lng, lat)
    }
  }, [location, mapInstance, updateSaleMarker])

  // Get initial map center
  const initialCenter = eventLocation 
    ? [parseFloat(eventLocation.longitude), parseFloat(eventLocation.latitude)]
    : [-98.5795, 39.8283]

  return (
    <div>
      <div className="mb-4">
        <LocationSearch
          onLocationSelect={handleLocationSearchSelect}
          eventLocation={eventLocation}
          radius={radius}
          placeholder="Search for a sale location..."
        />
      </div>

      <BaseMap
        center={initialCenter}
        zoom={eventLocation ? 12 : 3}
        onClick={handleMapClick}
        onMapReady={handleMapReady}
      >
        <RadiusCircle
          center={eventLocation ? [parseFloat(eventLocation.longitude), parseFloat(eventLocation.latitude)] : null}
          radius={radius}
          visible={!!eventLocation}
        />
      </BaseMap>

      {location?.address && (
        <div className="mt-2 p-2 bg-gray-50 rounded-md border border-gray-200">
          <p className="text-sm text-gray-700">
            <span className="font-medium">Selected location:</span>{' '}
            {location.address}, {location.city}, {location.state} {location.zip_code}
          </p>
        </div>
      )}

      {searchError && (
        <p className="mt-2 text-sm text-red-600">
          {searchError}
        </p>
      )}

      {formState?.errors?.['location.latitude'] || formState?.errors?.['location.longitude'] ? (
        <p className="mt-2 text-sm text-red-600">
          Please select a location on the map
        </p>
      ) : null}
    </div>
  )
}

export default SaleMap 